// Added by Sejune Oh on 2019-05-09
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { Component } from 'react'
import { RouterProps } from 'react-router'
import { action, makeObservable, observable } from 'mobx'
import { observer, inject } from 'mobx-react'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Intl
import Intl from '@/helper/Intl'
import {
  routerPath,
  mgmtSubPath,
  USER_ACCESS_LEVEL,
  UserAccessLevelProps,
  ENUM_USER_ACCESS_LEVEL,
} from '@/helper/define'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Components
import { getTableHeaderOption, TableUrlQueryProps, CustomSetterProps } from '@/components/common/Table'
import { MgmtListTemplate } from '@/components/common/Template'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Store
import { UserStore, DisplableUserElementProps } from '@/stores/mgmt/user'
import ProductStore from '@/stores/ProductStore'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
interface UserListFCProps extends RouterProps, TableUrlQueryProps {
  product?: ProductStore
}

interface UserListTableCProps {
  user?: UserStore
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const UserListTemplate = MgmtListTemplate<UserListTableCProps, DisplableUserElementProps>('user')

@inject('product')
@observer
class UserList extends Component<UserListFCProps> {
  tableHeaderProps = [
    getTableHeaderOption({
      key: 'username',
      label: Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.column_user_name' }),
      isSortable: true,
    }),
    getTableHeaderOption({
      key: 'display_name',
      label: Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.column_user_disp_name' }),
      isSortable: true,
    }),
    getTableHeaderOption({
      key: 'email',
      label: Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.column_user_email' }),
      isSortable: true,
    }),
    getTableHeaderOption({
      key: 'access_level',
      disp_key: 'str_access_level',
      label: Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.column_user_access_level' }),
      isSortable: true,
    }),
    getTableHeaderOption({
      key: 'activated',
      disp_key: 'is_activated',
      label: Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.column_user_activated_status' }),
      isSortable: true,
    }),
  ]
  userText = Intl.getIntlText({ id: 'htmljs_react.mgmt.userlist.bread_crumb_text_user' }) as string
  userPath = `${routerPath.MGMT}/${mgmtSubPath.USERS}`
  customSetter?: CustomSetterProps

  mounted: boolean = false

  constructor(props: UserListFCProps) {
    super(props)
    makeObservable(this, {
      mounted: observable,
      componentDidMount: action,
    })
  }

  componentDidMount = () => {
    this.customSetter = {
      name: 'setStrActivated',
      args: [this._getWithUserLevelNames()],
    }

    this.mounted = true
  }

  render() {
    return (
      this.mounted && (
        <UserListTemplate
          {...this.props}
          curPathSet={[this.userText, this.userPath]}
          headerProps={this.tableHeaderProps}
          customSetter={this.customSetter}
          isSortable={true}
          isSelectable={false}
          isPaginationable={true}
        />
      )
    )
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////`
  _getWithUserLevelNames = () => {
    if (!this.props.product!.userLevelNames) {
      return [USER_ACCESS_LEVEL.AccessLevelSysAdmin]
    }

    const acceeLevelList: Array<UserAccessLevelProps> = []
    for (let enumKey in this.props.product!.userLevelNames) {
      acceeLevelList.push({
        ...ENUM_USER_ACCESS_LEVEL[enumKey],
        label: this.props.product!.userLevelNames[enumKey],
      })
    }
    return acceeLevelList
  }
}

export default UserList
